import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { RateBoostLimitCard } from "@src/appV2/FacilityDashboard/VariableCharge/RateBoostLimitCard";
import { Text } from "@src/appV2/redesign/components/Text";
import { InviteHourlyBonusStepper } from "./InviteHourlyBonusStepper";

interface InviteChargeRateRowProps {
  hourlyBonusInMinorUnits: number;
  maxBoostInMinorUnits?: number;
  professionalBonusDisplay?: string;
  isChargeRateLoading: boolean;
  onBoostChange: (boostInMinorUnits: number) => void;
  /** Whether the current boost would push the workplace's weekly spend over its limit */
  isBoostOverLimit?: boolean;
  boostOverageInMinorUnits?: number;
  isBoostIncreaseRequested?: boolean;
  onRequestBoostIncrease?: () => void;
}

export function InviteChargeRateRow(props: InviteChargeRateRowProps) {
  const { tu } = useTu("schedule");

  const {
    hourlyBonusInMinorUnits,
    maxBoostInMinorUnits,
    professionalBonusDisplay,
    isChargeRateLoading,
    onBoostChange,
    isBoostOverLimit = false,
    boostOverageInMinorUnits = 0,
    isBoostIncreaseRequested = false,
    onRequestBoostIncrease,
  } = props;

  const isReady = isChargeRateLoading === false && isDefined(maxBoostInMinorUnits);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          padding: 3,
          backgroundColor: theme.palette.background?.standout,
        })}
      >
        <Stack
          direction="row"
          spacing={3}
          sx={{ flex: 1, alignItems: "stretch", justifyContent: "space-between" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Text variant="caption" sx={(theme) => ({ color: theme.palette.base?.muted })}>
              {tu("Hourly bonus")}
            </Text>
            <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
              {isReady ? (
                <InviteHourlyBonusStepper
                  boostInMinorUnits={hourlyBonusInMinorUnits}
                  maxBoostInMinorUnits={maxBoostInMinorUnits}
                  onBoostChange={onBoostChange}
                />
              ) : (
                <Skeleton variant="text" width={96} height={32} />
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Text variant="caption" sx={(theme) => ({ color: theme.palette.primary.main })}>
              {tu("Professional gets")}
            </Text>
            <Box
              sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "flex-end" }}
            >
              {isReady && isDefined(professionalBonusDisplay) ? (
                <Text
                  medium
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.base?.strong })}
                >
                  {professionalBonusDisplay}
                </Text>
              ) : (
                <Skeleton variant="text" width={64} height={18} />
              )}
            </Box>
          </Box>
        </Stack>
      </Box>

      {isReady && isBoostOverLimit && (
        <Box sx={{ paddingX: 3, paddingBottom: 3 }}>
          <RateBoostLimitCard
            isRequested={isBoostIncreaseRequested}
            overageInMinorUnits={boostOverageInMinorUnits}
            onRequestIncrease={onRequestBoostIncrease}
          />
        </Box>
      )}
    </Box>
  );
}
